import * as fileApi from '@/api/file';
import * as chemistryApi from '@/api/chemistry';
import type { Ketcher } from 'ketcher-core';
import { App, Button, Input, InputRef, Modal, Select, UploadFile } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import FolderSelector from './folderSelector';
import { useTranslation } from 'react-i18next';
import useFileStore from '@/utils/fileStore';

type ExportFormat =
    | 'smiles'
    | 'smiles_ext'
    | 'mol_v2000'
    | 'mol_v3000'
    | 'rxn_v2000'
    | 'rxn_v3000'
    | 'ket'
    | 'smarts'
    | 'cml'
    | 'sdf'
    | 'rdf';

type OBabelFormat = 'xyz' | 'pdb' | 'mol2' | 'pqr' | 'cube' | 'cif' | 'gro' | 'inchi';

type AnyFormat = ExportFormat | OBabelFormat;

interface FormatOption {
    label: string;
    value: AnyFormat;
    extension: string;
}

const FORMAT_OPTIONS: FormatOption[] = [
    { label: 'SMILES (.smi)', value: 'smiles', extension: '.smi' },
    { label: 'Extended SMILES (.smi)', value: 'smiles_ext', extension: '.smi' },
    { label: 'MOL v2000 (.mol)', value: 'mol_v2000', extension: '.mol' },
    { label: 'MOL v3000 (.mol)', value: 'mol_v3000', extension: '.mol' },
    { label: 'RXN v2000 (.rxn)', value: 'rxn_v2000', extension: '.rxn' },
    { label: 'RXN v3000 (.rxn)', value: 'rxn_v3000', extension: '.rxn' },
    { label: 'KET (.ket)', value: 'ket', extension: '.ket' },
    { label: 'SMARTS (.sma)', value: 'smarts', extension: '.sma' },
    { label: 'CML (.cml)', value: 'cml', extension: '.cml' },
    { label: 'SDF (.sdf)', value: 'sdf', extension: '.sdf' },
    { label: 'RDF (.rdf)', value: 'rdf', extension: '.rdf' },
];

const OBABEL_FORMAT_OPTIONS: FormatOption[] = [
    { label: 'XYZ (.xyz)', value: 'xyz', extension: '.xyz' },
    { label: 'PDB (.pdb)', value: 'pdb', extension: '.pdb' },
    { label: 'Mol2 (.mol2)', value: 'mol2', extension: '.mol2' },
    { label: 'PQR (.pqr)', value: 'pqr', extension: '.pqr' },
    { label: 'Gaussian Cube (.cube)', value: 'cube', extension: '.cube' },
    { label: 'CIF (.cif)', value: 'cif', extension: '.cif' },
    { label: 'GRO (.gro)', value: 'gro', extension: '.gro' },
    { label: 'InChI (.inchi)', value: 'inchi', extension: '.inchi' },
];

const ALL_FORMAT_OPTIONS = [...FORMAT_OPTIONS, ...OBABEL_FORMAT_OPTIONS];

const isNativeFormat = (fmt: AnyFormat): fmt is ExportFormat =>
    FORMAT_OPTIONS.some((f) => f.value === fmt);

const getExtension = (format: AnyFormat): string =>
    ALL_FORMAT_OPTIONS.find((f) => f.value === format)?.extension ?? '';

const getContent = async (ketcher: Ketcher, format: ExportFormat): Promise<string> => {
    switch (format) {
        case 'smiles':
            return ketcher.getSmiles();
        case 'smiles_ext':
            return ketcher.getSmiles(true);
        case 'mol_v2000':
            return ketcher.getMolfile('v2000');
        case 'mol_v3000':
            return ketcher.getMolfile('v3000');
        case 'rxn_v2000':
            return ketcher.getRxn('v2000');
        case 'rxn_v3000':
            return ketcher.getRxn('v3000');
        case 'ket':
            return ketcher.getKet();
        case 'smarts':
            return ketcher.getSmarts();
        case 'cml':
            return ketcher.getCml();
        case 'sdf':
            return ketcher.getSdf();
        case 'rdf':
            return ketcher.getRdf();
    }
};

interface KetcherExportDialogProps {
    visible: boolean;
    ketcher: Ketcher | null;
    onClose: () => void;
}

const KetcherExportDialog: React.FC<KetcherExportDialogProps> = ({ visible, ketcher, onClose }) => {
    const { message } = App.useApp();
    const [format, setFormat] = useState<AnyFormat>('mol_v2000');
    const [fileName, setFileName] = useState<string>('molecule.mol');
    const [savePath, setSavePath] = useState<string | undefined>();
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<InputRef>(null);
    const { t } = useTranslation('common');
    const baseHome = useFileStore((state) => state.baseHome);
    const updateFolder = useFileStore((state) => state.updateFolder);

    // Update filename extension when format changes
    useEffect(() => {
        setFileName((prev) => {
            if (!prev) return prev;
            const newExt = getExtension(format);
            const withoutExt = prev.replace(/\.[^.]+$/, '');
            return withoutExt + newExt;
        });
    }, [format]);

    // Reset state when dialog opens
    useEffect(() => {
        if (visible) {
            setFormat('mol_v2000');
            setFileName('molecule.mol');
            setSavePath(undefined);
            setTimeout(() => {
                inputRef.current?.focus({ cursor: 'all' });
            }, 100);
        }
    }, [visible]);

    const handleSave = async () => {
        if (!ketcher) {
            message.error(t('ketcher_export.ketcher_not_ready', 'Ketcher is not ready'));
            return;
        }

        const trimmedName = fileName.trim();
        if (!trimmedName) {
            message.error(t('ketcher_export.name_required', 'Please enter a file name'));
            return;
        }

        const dir = savePath ?? baseHome;
        if (!dir) {
            message.error(t('ketcher_export.dir_required', 'Please select a save location'));
            return;
        }

        setSaving(true);
        try {
            if (isNativeFormat(format)) {
                // Native Ketcher format: get content and upload directly
                const content = await getContent(ketcher, format);
                const file = new File([content], trimmedName, { type: 'text/plain' });
                const uploadFile: UploadFile = {
                    uid: Date.now().toString(),
                    name: trimmedName,
                    originFileObj: file as any,
                    status: 'done',
                    url: '',
                };
                const res = await fileApi.uploadFileResource([uploadFile], dir);
                if (res?.ok) {
                    message.success(t('ketcher_export.success', 'File saved successfully'));
                    updateFolder(dir);
                    onClose();
                } else {
                    message.error(res?.message || t('ketcher_export.failed', 'Failed to save file'));
                }
            } else {
                // Non-native format: export as SMILES, upload temp file, convert via OpenBabel
                const smiles = await ketcher.getSmiles();
                const tempFileName = `.tmp_ketcher_export_${Date.now()}.smi`;
                const tempPath = `${dir}/${tempFileName}`;

                const tempFile = new File([smiles], tempFileName, { type: 'text/plain' });
                const tempUploadFile: UploadFile = {
                    uid: Date.now().toString(),
                    name: tempFileName,
                    originFileObj: tempFile as any,
                    status: 'done',
                    url: '',
                };
                const uploadRes = await fileApi.uploadFileResource([tempUploadFile], dir, true);
                if (!uploadRes?.ok) {
                    message.error(uploadRes?.message || t('ketcher_export.failed', 'Failed to save file'));
                    return;
                }

                const outputPath = `${dir}/${trimmedName}`;
                const convertRes = await chemistryApi.convertMolecule({
                    path: tempPath,
                    to_format: format,
                    output_path: outputPath,
                });

                // Clean up temp SMILES file
                try { await fileApi.deleteFileResource([tempPath]); } catch (_) { }

                if (convertRes?.ok) {
                    message.success(t('ketcher_export.success', 'File saved successfully'));
                    updateFolder(dir);
                    onClose();
                } else {
                    message.error(convertRes?.message || t('ketcher_export.failed', 'Failed to save file'));
                }
            }
        } catch (err: any) {
            message.error(err?.message || t('ketcher_export.failed', 'Failed to save file'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={t('ketcher_export.title', 'Export Molecule')}
            open={visible}
            destroyOnHidden
            onCancel={onClose}
            width={600}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    {t('ketcher_export.cancel', 'Cancel')}
                </Button>,
                <Button key="save" type="primary" loading={saving} onClick={handleSave}>
                    {t('ketcher_export.save', 'Save')}
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-4 py-2">
                <div>
                    <div className="mb-1 text-sm font-medium">
                        {t('ketcher_export.format', 'Format')}
                    </div>
                    <Select
                        className="w-full"
                        value={format}
                        showSearch
                        onChange={(val) => setFormat(val)}
                        options={[...FORMAT_OPTIONS, ...OBABEL_FORMAT_OPTIONS]}
                    />
                </div>

                <div>
                    <div className="mb-1 text-sm font-medium">
                        {t('ketcher_export.filename', 'File Name')}
                    </div>
                    <Input
                        ref={inputRef}
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        onPressEnter={handleSave}
                    />
                </div>

                <div>
                    <div className="mb-1 text-sm font-medium">
                        {t('ketcher_export.save_location', 'Save Location')}
                    </div>
                    <div className="border rounded max-h-64 overflow-y-auto">
                        <FolderSelector onSelectDirectory={setSavePath} />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default KetcherExportDialog;
